import React, { useState } from 'react';
import useEscapeKey from '../hooks/useEscapeKey';
import DateInput from './DateInput';
import { partTimeFraction, partTimePatternLabel, formatCurrency } from '../utils/pensionCalc';

// Quick-set rises. "Give them 10%" is what people actually mean, and applying it to the
// pattern rather than to one of the two salary columns removes the chance of raising the
// wrong number.
const QUICK_RISES = [3, 5, 10];

export default function SalaryChangeModal({ employee, onSave, onClose }) {
  useEscapeKey(onClose);
  const isHourly = employee.pay_type === 'hourly';
  const [form, setForm] = useState({
    effective_date: '',
    new_annual_salary: '',
    new_hourly_rate: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const currentSalary = parseFloat(employee.annual_salary) || 0;
  const currentHourlyRate = parseFloat(employee.hourly_rate) || 0;

  // Part-time context. annual_salary is the full-time equivalent; what the employee is
  // actually paid is that scaled by their working pattern. Showing the FTE as "current
  // salary" (as this modal used to) states a figure they never receive.
  const ptFraction = partTimeFraction(employee);
  const isPartTimeSalaried = !isHourly && ptFraction < 1;
  const patternLabel = partTimePatternLabel(employee);
  const currentProRata = currentSalary * ptFraction;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setError('');
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const newSalary = parseFloat(form.new_annual_salary) || 0;
  const newHourlyRate = parseFloat(form.new_hourly_rate) || 0;
  const salaryDiff = newSalary > 0 ? newSalary - currentSalary : 0;
  const rateDiff = newHourlyRate > 0 ? newHourlyRate - currentHourlyRate : 0;

  // The figure the employee will actually be paid, derived from the entered FTE and the
  // unchanged working pattern. Shown before submitting so a wrong entry is caught here.
  const newProRata = newSalary > 0 ? newSalary * ptFraction : 0;
  const proRataDiff = newProRata > 0 ? newProRata - currentProRata : 0;
  const pctChange = currentSalary > 0 && newSalary > 0
    ? ((newSalary - currentSalary) / currentSalary) * 100
    : 0;

  const applyQuickRise = (pct) => {
    setError('');
    const raised = Math.round(currentSalary * (1 + pct / 100) * 100) / 100;
    setForm(prev => ({ ...prev, new_annual_salary: String(raised) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.effective_date) {
      setError('Please enter an effective date');
      return;
    }

    if (isHourly) {
      if (!form.new_hourly_rate || newHourlyRate <= 0) {
        setError('Please enter a valid new hourly rate');
        return;
      }
      if (newHourlyRate === currentHourlyRate) {
        setError('New hourly rate is the same as the current rate');
        return;
      }
    } else {
      if (!form.new_annual_salary || newSalary <= 0) {
        setError('Please enter a valid new salary');
        return;
      }
      if (newSalary === currentSalary) {
        setError('New salary is the same as the current salary');
        return;
      }
    }

    setSaving(true);
    try {
      if (isHourly) {
        await onSave(employee.id, {
          effective_date: form.effective_date,
          new_hourly_rate: form.new_hourly_rate,
          previous_hourly_rate: currentHourlyRate.toFixed(2),
          pay_type: 'hourly',
          notes: form.notes,
        });
      } else {
        await onSave(employee.id, {
          effective_date: form.effective_date,
          new_annual_salary: form.new_annual_salary,
          // new_monthly_salary is what the processor keys, so for a part-timer it must be
          // the pro-rata month — not the FTE month, which is a figure nobody is paid.
          new_monthly_salary: (newProRata / 12).toFixed(2),
          previous_salary: currentSalary.toFixed(2),
          ...(isPartTimeSalaried ? {
            is_part_time: true,
            pt_fraction: Math.round(ptFraction * 10000) / 10000,
            pt_pattern: patternLabel,
            previous_prorata_salary: currentProRata.toFixed(2),
            new_prorata_salary: newProRata.toFixed(2),
            previous_fte_salary: currentSalary.toFixed(2),
            new_fte_salary: newSalary.toFixed(2),
          } : {}),
          notes: form.notes,
        });
      }
      onClose();
    } catch (err) {
      setError('Failed to save: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full dark:bg-gray-800" onClick={e => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {isHourly ? 'Hourly Rate Change' : 'Salary Change'} - {employee.first_name} {employee.last_name}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full p-2 text-2xl leading-none" aria-label="Close">&times;</button>
          </div>

          {error && <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded text-sm text-red-700 dark:text-red-300">{error}</div>}

          <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
            {isHourly ? (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400">Current hourly rate</p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  &pound;{currentHourlyRate.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/hr
                  <span className="text-sm font-normal text-gray-400 ml-1">
                    ({parseFloat(employee.contracted_hours_per_week) > 0 ? `${employee.contracted_hours_per_week} hrs/wk` : 'Zero hours'})
                  </span>
                </p>
              </>
            ) : (
              <>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {isPartTimeSalaried ? 'Current pay' : 'Current salary'}
                </p>
                <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {formatCurrency(isPartTimeSalaried ? currentProRata : currentSalary, { decimals: 'auto' })}
                  <span className="text-sm font-normal text-gray-400 ml-1">
                    ({formatCurrency((isPartTimeSalaried ? currentProRata : currentSalary) / 12)}/mo)
                  </span>
                </p>
                {isPartTimeSalaried && (
                  <>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Part-time{patternLabel ? ` — ${patternLabel}` : ''}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Full-time equivalent {formatCurrency(currentSalary, { decimals: 'auto' })}
                    </p>
                  </>
                )}
              </>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {isHourly ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">New Hourly Rate *</label>
                <div className="mt-1 relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                  <input type="number" name="new_hourly_rate" step="0.01" min="0" inputMode="decimal" value={form.new_hourly_rate} onChange={handleChange}
                    className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="0.00" autoFocus />
                </div>
                {newHourlyRate > 0 && rateDiff !== 0 && (
                  <p className={`text-xs mt-1 ${rateDiff > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {rateDiff > 0 ? '+' : ''}&pound;{rateDiff.toFixed(2)}/hr ({rateDiff > 0 ? '+' : ''}{((rateDiff / currentHourlyRate) * 100).toFixed(1)}%)
                  </p>
                )}
              </div>
            ) : (
              <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isPartTimeSalaried ? 'New full-time equivalent salary *' : 'New Annual Salary *'}
                </label>
                <div className="mt-1 relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400 text-sm">&pound;</span>
                  <input type="number" name="new_annual_salary" step="0.01" min="0" inputMode="decimal" value={form.new_annual_salary} onChange={handleChange}
                    className="block w-full pl-7 rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="0.00" autoFocus />
                </div>
                {currentSalary > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {QUICK_RISES.map(pct => (
                      <button key={pct} type="button" onClick={() => applyQuickRise(pct)}
                        className="px-2.5 py-1 text-xs font-medium rounded-full border border-gray-300 text-gray-600 hover:border-brand-400 hover:text-brand-700 dark:border-gray-600 dark:text-gray-300 dark:hover:border-brand-500">
                        +{pct}%
                      </button>
                    ))}
                  </div>
                )}
                {newSalary > 0 && salaryDiff !== 0 && (
                  <p className={`text-xs mt-2 ${salaryDiff > 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {isPartTimeSalaried ? 'FTE ' : ''}{salaryDiff > 0 ? '+' : ''}{formatCurrency(salaryDiff, { decimals: 'auto' })} ({pctChange > 0 ? '+' : ''}{pctChange.toFixed(1)}%)
                  </p>
                )}
              </div>

              {/* The figure that actually reaches the payslip. A salary change on a
                  part-timer used to show only the FTE, so there was no way to see what
                  the employee would be paid before submitting. */}
              {isPartTimeSalaried && newProRata > 0 && (
                <div className="p-3 rounded-lg border border-brand-200 bg-brand-50 dark:border-brand-800 dark:bg-brand-900/30">
                  <p className="text-xs uppercase tracking-wide text-brand-700 dark:text-brand-300">
                    {employee.first_name} will be paid
                  </p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {formatCurrency(newProRata, { decimals: 'auto' })}
                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-1">
                      ({formatCurrency(newProRata / 12)}/mo)
                    </span>
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    {proRataDiff >= 0 ? '+' : ''}{formatCurrency(proRataDiff, { decimals: 'auto' })}/yr
                    {' · '}{proRataDiff >= 0 ? '+' : ''}{formatCurrency(proRataDiff / 12)}/mo
                    {patternLabel ? ` · pattern unchanged at ${Math.round(ptFraction * 1000) / 10}%` : ''}
                  </p>
                </div>
              )}
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Effective Date *</label>
              <DateInput name="effective_date" value={form.effective_date} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white" />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Notes</label>
              <textarea name="notes" rows="2" value={form.notes} onChange={handleChange}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                placeholder={isHourly ? 'e.g. NMW increase, pay review...' : 'e.g. Annual pay review, promotion...'} />
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded text-sm text-blue-700 dark:text-blue-300">
              This {isHourly ? 'rate' : 'salary'} change for {employee.first_name} {employee.last_name} will be included in the next payroll submission.
            </div>

            <div className="flex justify-end space-x-3 pt-2">
              <button type="button" onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600">
                Cancel
              </button>
              <button type="submit" disabled={saving}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Saving...' : 'Submit Change'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
